import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import intlShape from '../../lib/intlShape.js';
import log from '../../lib/log.js';
import {
    clearDomiCarBluetoothBonds,
    configureDomiCarWirelessMode,
    describeSerialPort,
    fetchFirmwareFiles,
    fetchFirmwareManifest,
    flashDomiCarFirmware,
    isWebSerialSupported,
    provisionDomiCarWifi,
    readDomiCarInfo,
    requestDomiCarPort,
    verifyDomiCarConnection
} from '../../lib/domi-car-flasher.js';

import styles from './domi-device-center.css';

const messages = defineMessages({
    title: {
        id: 'gui.domiDeviceCenter.title',
        defaultMessage: 'Domi Device Center',
        description: 'Title of the Domi hardware device center'
    },
    connectCancelled: {
        id: 'gui.domiDeviceCenter.connectCancelled',
        defaultMessage: 'No USB device was selected.',
        description: 'Error when the USB serial picker is cancelled'
    },
    serialUnavailable: {
        id: 'gui.domiDeviceCenter.serialUnavailable',
        defaultMessage: 'Use desktop Chrome or Edge over HTTPS to install firmware.',
        description: 'Message shown when Web Serial is unavailable'
    },
    portBusy: {
        id: 'gui.domiDeviceCenter.portBusy',
        defaultMessage: 'The USB port is busy. Close Arduino Serial Monitor and try again.',
        description: 'Message shown when another program has opened the serial port'
    },
    downloadMode: {
        id: 'gui.domiDeviceCenter.downloadMode',
        defaultMessage: 'Could not enter download mode. Hold IO0, tap RESET, then try again.',
        description: 'Message shown when an ESP32 cannot enter download mode'
    },
    unstableSerial: {
        id: 'gui.domiDeviceCenter.unstableSerial',
        defaultMessage: 'USB 串口通信不稳定，已将网站固件的烧录速度降至 115200。请重新选择 USB 小车后再安装。',
        description: 'Message shown when a firmware flash fails because the serial speed is unstable'
    },
    firmwareUnavailable: {
        id: 'gui.domiDeviceCenter.firmwareUnavailable',
        defaultMessage: 'The Domi firmware could not be downloaded. Check the network or choose a local .bin file.',
        description: 'Message shown when the firmware file cannot be loaded'
    },
    wifiTimeout: {
        id: 'gui.domiDeviceCenter.wifiTimeout',
        defaultMessage: 'The car did not connect to Wi-Fi. Check the network name and password.',
        description: 'Message shown when Wi-Fi provisioning times out'
    },
    carHttpUnreachable: {
        id: 'gui.domiDeviceCenter.carHttpUnreachable',
        defaultMessage: '小车已经获得 IP，但网页无法访问小车。请确认电脑和小车连接同一个 2.4 GHz Wi-Fi。',
        description: 'Message shown when the car has an IP but its HTTP control service cannot be reached'
    },
    genericError: {
        id: 'gui.domiDeviceCenter.genericError',
        defaultMessage: 'Operation failed: {message}',
        description: 'Generic device center error'
    }
});

const PHASE_IDLE = 'idle';
const PHASE_LOADING = 'loading';
const PHASE_INSPECTING = 'inspecting';
const PHASE_FLASHING = 'flashing';
const PHASE_FLASHED = 'flashed';
const PHASE_PROVISIONING = 'provisioning';
const PHASE_DONE = 'done';
const PHASE_ERROR = 'error';
const DOMI_CAR_ADDRESS_KEY = 'domiCar.address';
const DOMI_DEVICE_CENTER_SESSION_KEY = 'domiDeviceCenter.lastSession';
const DOMI_CAR_ADDRESS_CHANGED = 'DOMI_CAR_ADDRESS_CHANGED';
const WIRELESS_MODE_WIFI = 'wifi';
const WIRELESS_MODE_BLUETOOTH = 'bluetooth';

const supportsExclusiveWirelessMode = info => Boolean(
    info &&
    Array.isArray(info.capabilities) &&
    info.capabilities.includes('exclusiveWirelessMode')
);

const saveDomiCarAddress = function (address) {
    try {
        window.localStorage.setItem(DOMI_CAR_ADDRESS_KEY, address);
    } catch (error) {
        log.warn(`Domi Device Center could not save the car address: ${error.message}`);
    }
};

const clearDomiCarAddress = function () {
    try {
        window.localStorage.removeItem(DOMI_CAR_ADDRESS_KEY);
    } catch (error) {
        log.warn(`Domi Device Center could not clear the car address: ${error.message}`);
    }
};

const loadDeviceCenterSession = function () {
    try {
        const savedSession = JSON.parse(window.localStorage.getItem(DOMI_DEVICE_CENTER_SESSION_KEY));
        if (!savedSession || typeof savedSession !== 'object') return {};
        return {
            deviceInfo: savedSession.deviceInfo || null,
            errorMessage: '',
            flashComplete: savedSession.flashComplete === true,
            ipAddress: typeof savedSession.ipAddress === 'string' ? savedSession.ipAddress : '',
            logs: Array.isArray(savedSession.logs) ? savedSession.logs.map(String).slice(-60) : [],
            phase: PHASE_IDLE,
            progress: Number.isFinite(savedSession.progress) ? savedSession.progress : 0,
            ssid: typeof savedSession.ssid === 'string' ? savedSession.ssid : '',
            wirelessMode: savedSession.wirelessMode === WIRELESS_MODE_BLUETOOTH ?
                WIRELESS_MODE_BLUETOOTH : WIRELESS_MODE_WIFI
        };
    } catch (error) {
        log.warn(`Domi Device Center could not restore its last session: ${error.message}`);
        return {};
    }
};

const saveDeviceCenterSession = function (state) {
    try {
        window.localStorage.setItem(DOMI_DEVICE_CENTER_SESSION_KEY, JSON.stringify({
            deviceInfo: state.deviceInfo,
            errorMessage: state.errorMessage,
            flashComplete: state.flashComplete,
            ipAddress: state.ipAddress,
            logs: state.logs,
            phase: state.phase,
            progress: state.progress,
            ssid: state.ssid,
            wirelessMode: state.wirelessMode
        }));
    } catch (error) {
        log.warn(`Domi Device Center could not save its last session: ${error.message}`);
    }
};

class DomiDeviceCenter extends React.Component {
    constructor (props) {
        super(props);
        const savedSession = loadDeviceCenterSession();
        bindAll(this, [
            'appendLog',
            'handleClearBluetoothBonds',
            'handleConnect',
            'handleFirmwareFileChange',
            'handleFlash',
            'handlePasswordChange',
            'handleRequestClose',
            'handleSsidChange',
            'handleWirelessModeChange',
            'handleWifiProvision',
            'loadManifest'
        ]);
        this.state = {
            bluetoothBondsCleared: false,
            customFirmwareFile: null,
            deviceInfo: savedSession.deviceInfo || null,
            errorMessage: savedSession.errorMessage || '',
            flashComplete: savedSession.flashComplete || false,
            firmwareInfo: null,
            firmwareProbeError: '',
            ipAddress: savedSession.ipAddress || '',
            logs: savedSession.logs || [],
            manifest: null,
            password: '',
            phase: savedSession.phase || PHASE_IDLE,
            port: null,
            progress: savedSession.progress || 0,
            ssid: savedSession.ssid || '',
            wirelessMode: savedSession.wirelessMode || WIRELESS_MODE_WIFI,
            webSerialSupported: isWebSerialSupported()
        };
    }
    componentDidMount () {
        this._isMounted = true;
        this.loadManifest();
    }
    componentDidUpdate () {
        saveDeviceCenterSession(this.state);
    }
    componentWillUnmount () {
        saveDeviceCenterSession(this.state);
        this._isMounted = false;
    }
    get isBusy () {
        return [PHASE_INSPECTING, PHASE_LOADING, PHASE_FLASHING, PHASE_PROVISIONING].includes(this.state.phase);
    }
    async loadManifest () {
        try {
            const manifest = await fetchFirmwareManifest();
            if (this._isMounted) this.setState({manifest});
        } catch (error) {
            this.appendLog('没有加载到在线固件，可选择本地合并固件。');
        }
    }
    appendLog (line) {
        if (!this._isMounted || !line) return;
        this.setState(previousState => ({
            logs: previousState.logs.concat(String(line)).slice(-60)
        }));
    }
    getErrorMessage (error) {
        const code = error && error.message ? error.message : String(error);
        if (error && error.name === 'NotFoundError') {
            return this.props.intl.formatMessage(messages.connectCancelled);
        }
        if (code === 'WEB_SERIAL_UNAVAILABLE') {
            return this.props.intl.formatMessage(messages.serialUnavailable);
        }
        if (/already open|Failed to open|NetworkError/i.test(code)) {
            return this.props.intl.formatMessage(messages.portBusy);
        }
        if (code === 'WIFI_CONNECT_TIMEOUT') {
            return this.props.intl.formatMessage(messages.wifiTimeout);
        }
        if (code === 'CAR_HTTP_UNREACHABLE') {
            return this.props.intl.formatMessage(messages.carHttpUnreachable);
        }
        if (code === 'MODE_SWITCH_TIMEOUT') {
            return '小车没有确认模式切换，请重新选择 USB 小车后再试。';
        }
        if (/lower baud|Invalid head of packet|serial noise|corruption/i.test(code)) {
            return this.props.intl.formatMessage(messages.unstableSerial);
        }
        if (/Failed to connect|packet header|download mode|Timeout/i.test(code)) {
            return this.props.intl.formatMessage(messages.downloadMode);
        }
        if (/FIRMWARE_|fetch|network/i.test(code)) {
            return this.props.intl.formatMessage(messages.firmwareUnavailable);
        }
        return this.props.intl.formatMessage(messages.genericError, {message: code});
    }
    async handleConnect () {
        this.setState({
            bluetoothBondsCleared: false,
            errorMessage: '',
            firmwareInfo: null,
            firmwareProbeError: '',
            phase: PHASE_INSPECTING,
            port: null
        });
        try {
            const port = await requestDomiCarPort();
            const deviceInfo = describeSerialPort(port);
            this.appendLog(`USB 串口已选择：VID ${deviceInfo.vendorLabel} / PID ${deviceInfo.productLabel}`);
            let firmwareInfo = null;
            let firmwareProbeError = '';
            try {
                firmwareInfo = await readDomiCarInfo({port, onLog: this.appendLog});
                this.appendLog(`已检测固件 v${firmwareInfo.firmwareVersion || '未知版本'}`);
            } catch (error) {
                firmwareProbeError = error && error.message ? error.message : String(error);
                this.appendLog(`无法识别当前固件：${firmwareProbeError}`);
            }
            if (!this._isMounted) return;
            this.setState({
                deviceInfo,
                firmwareInfo,
                firmwareProbeError,
                phase: PHASE_IDLE,
                port
            });
        } catch (error) {
            if (this._isMounted) {
                this.setState({
                    errorMessage: this.getErrorMessage(error),
                    firmwareProbeError: '',
                    phase: PHASE_ERROR
                });
            }
        }
    }
    handleFirmwareFileChange (event) {
        const customFirmwareFile = event.target.files && event.target.files[0];
        this.setState({customFirmwareFile: customFirmwareFile || null});
    }
    async handleFlash () {
        const {customFirmwareFile, manifest, port} = this.state;
        if (!port || (!manifest && !customFirmwareFile)) return;

        this.setState({
            errorMessage: '',
            flashComplete: false,
            phase: PHASE_LOADING,
            progress: 0
        });
        this.appendLog('正在准备并校验固件…');

        try {
            let activeManifest = manifest;
            let files;
            if (customFirmwareFile) {
                activeManifest = {
                    device: 'DOMI_CAR',
                    version: customFirmwareFile.name,
                    baudRate: 115200,
                    flashMode: 'dio',
                    flashFrequency: '40m',
                    flashSize: '4MB',
                    eraseAll: true,
                    files: [{address: 0, url: 'local'}]
                };
                files = [{
                    address: 0,
                    data: new Uint8Array(await customFirmwareFile.arrayBuffer())
                }];
            } else {
                files = await fetchFirmwareFiles(activeManifest);
            }

            if (!this._isMounted) {
                return;
            }
            this.setState({phase: PHASE_FLASHING});
            await flashDomiCarFirmware({
                port,
                manifest: activeManifest,
                files,
                onLog: this.appendLog,
                onProgress: ({written, total}) => {
                    if (!this._isMounted || !total) {
                        return;
                    }
                    this.setState({progress: Math.min(100, Math.round((written / total) * 100))});
                }
            });

            if (this._isMounted) {
                this.setState({
                    flashComplete: true,
                    firmwareInfo: customFirmwareFile ? null : {
                        capabilities: activeManifest.capabilities || [],
                        firmwareVersion: activeManifest.version,
                        wirelessMode: 'WIFI'
                    },
                    firmwareProbeError: customFirmwareFile ? 'CUSTOM_FIRMWARE_REQUIRES_RECHECK' : '',
                    phase: PHASE_FLASHED,
                    progress: 100
                });
            }
        } catch (error) {
            this.appendLog(error && error.message ? error.message : String(error));
            if (this._isMounted) {
                this.setState({
                    errorMessage: this.getErrorMessage(error),
                    phase: PHASE_ERROR
                });
            }
        }
    }
    handleSsidChange (event) {
        this.setState({ssid: event.target.value});
    }
    handlePasswordChange (event) {
        this.setState({password: event.target.value});
    }
    handleWirelessModeChange (event) {
        this.setState({
            bluetoothBondsCleared: false,
            errorMessage: '',
            phase: PHASE_IDLE,
            wirelessMode: event.currentTarget.value
        });
    }
    async handleClearBluetoothBonds () {
        this.setState({
            errorMessage: '',
            phase: PHASE_PROVISIONING
        });
        try {
            await clearDomiCarBluetoothBonds({
                port: this.state.port,
                onLog: this.appendLog
            });
            if (this._isMounted) this.setState({bluetoothBondsCleared: true, phase: PHASE_DONE});
        } catch (error) {
            this.appendLog(error && error.message ? error.message : String(error));
            if (this._isMounted) {
                this.setState({
                    errorMessage: this.getErrorMessage(error),
                    phase: PHASE_ERROR
                });
            }
        }
    }
    async handleWifiProvision () {
        if (!supportsExclusiveWirelessMode(this.state.firmwareInfo)) return;
        this.setState({
            errorMessage: '',
            phase: PHASE_PROVISIONING
        });
        try {
            if (this.state.wirelessMode === WIRELESS_MODE_BLUETOOTH) {
                await configureDomiCarWirelessMode({
                    port: this.state.port,
                    mode: WIRELESS_MODE_BLUETOOTH,
                    onLog: this.appendLog
                });
                if (this._isMounted) {
                    clearDomiCarAddress();
                    this.props.vm.runtime.emit(DOMI_CAR_ADDRESS_CHANGED, '');
                    this.setState({bluetoothBondsCleared: false, phase: PHASE_DONE});
                }
                return;
            }
            const result = await provisionDomiCarWifi({
                port: this.state.port,
                ssid: this.state.ssid,
                password: this.state.password,
                onLog: this.appendLog
            });
            this.appendLog(`小车已获得地址 ${result.ipAddress}，正在验证网页控制接口…`);
            await verifyDomiCarConnection(result.ipAddress);
            this.appendLog('小车网页控制接口验证成功。');
            if (this._isMounted) {
                saveDomiCarAddress(result.ipAddress);
                this.props.vm.runtime.emit(DOMI_CAR_ADDRESS_CHANGED, result.ipAddress);
                this.setState({
                    ipAddress: result.ipAddress,
                    phase: PHASE_DONE
                });
            }
        } catch (error) {
            this.appendLog(error && error.message ? error.message : String(error));
            if (this._isMounted) {
                this.setState({
                    errorMessage: this.getErrorMessage(error),
                    phase: PHASE_ERROR
                });
            }
        }
    }
    handleRequestClose () {
        if (!this.isBusy) {
            this.props.onRequestClose();
        }
    }
    render () {
        const {
            bluetoothBondsCleared,
            customFirmwareFile,
            deviceInfo,
            errorMessage,
            flashComplete,
            firmwareInfo,
            firmwareProbeError,
            ipAddress,
            logs,
            manifest,
            password,
            phase,
            port,
            progress,
            ssid,
            wirelessMode,
            webSerialSupported
        } = this.state;
        const canFlash = Boolean(port && (manifest || customFirmwareFile) && !this.isBusy);
        const firmwareSupportsModeSwitch = supportsExclusiveWirelessMode(firmwareInfo);
        const firmwareSupportsClassicBluetooth = Boolean(
            firmwareInfo && Array.isArray(firmwareInfo.capabilities) &&
            firmwareInfo.capabilities.includes('classicBluetoothSpp')
        );
        const canProvision = Boolean(port && firmwareSupportsModeSwitch && !this.isBusy && (
            wirelessMode === WIRELESS_MODE_BLUETOOTH || ssid.trim()
        ));

        return (
            <Modal
                className={styles.modal}
                contentLabel={this.props.intl.formatMessage(messages.title)}
                id="domi-device-center"
                onRequestClose={this.handleRequestClose}
            >
                <div className={styles.body}>
                    <div className={styles.intro}>
                        <div>
                            <h2>Domi 小车安装器</h2>
                            <p>首次使用按 1 → 2 → 3 操作；日常上课无需 USB，也不需要 Arduino IDE。</p>
                        </div>
                        <span className={webSerialSupported ? styles.readyBadge : styles.warningBadge}>
                            {webSerialSupported ? '浏览器支持 USB 烧录' : '当前浏览器不支持 Web Serial'}
                        </span>
                    </div>

                    {webSerialSupported ? null : (
                        <div className={styles.errorBox}>
                            {this.props.intl.formatMessage(messages.serialUnavailable)}
                        </div>
                    )}
                    {errorMessage ? <div className={styles.errorBox}>{errorMessage}</div> : null}

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.stepNumber}>1</div>
                            <div>
                                <h3>连接 USB 小车</h3>
                                <p>连接下载底板后，从浏览器弹窗选择 USB Serial/CH340。</p>
                            </div>
                        </div>
                        <div className={styles.actionRow}>
                            <button
                                className={styles.primaryButton}
                                disabled={!webSerialSupported || this.isBusy}
                                type="button"
                                onClick={this.handleConnect}
                            >
                                {phase === PHASE_INSPECTING ? '正在检测小车…' : (
                                    port ? '重新选择设备' : '选择 USB 小车'
                                )}
                            </button>
                            {port && deviceInfo ? (
                                <div className={styles.deviceDetails}>
                                    <strong>USB 串口已连接</strong>
                                    <span>VID {deviceInfo.vendorLabel} · PID {deviceInfo.productLabel}</span>
                                    {firmwareInfo ? (
                                        <span>
                                            当前固件 v{firmwareInfo.firmwareVersion || '未知'}
                                            {firmwareInfo.wirelessMode ? ` · ${firmwareInfo.wirelessMode} 模式` : ''}
                                        </span>
                                    ) : null}
                                </div>
                            ) : deviceInfo ? (
                                <div className={styles.deviceDetails}>
                                    <strong>上次选择的 USB 串口</strong>
                                    <span>
                                        VID {deviceInfo.vendorLabel} · PID {deviceInfo.productLabel}，继续操作前请重新选择
                                    </span>
                                </div>
                            ) : <span className={styles.muted}>尚未选择设备</span>}
                        </div>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.stepNumber}>2</div>
                            <div>
                                <h3>重新安装 Domi 固件（首次使用或修复时）</h3>
                                <p>这是真正的固件烧录，会清除原程序和 Wi-Fi；日常上课不需要重复操作。</p>
                            </div>
                        </div>
                        <div className={styles.firmwareSummary}>
                            <div>
                                <span className={styles.label}>网站可安装的固件（不代表设备当前版本）</span>
                                <strong>{manifest ? `${manifest.displayName} v${manifest.version}` : '尚未加载'}</strong>
                            </div>
                        </div>
                        <details className={styles.advancedOptions}>
                            <summary>高级选项：使用本地 .bin 固件</summary>
                            <label className={styles.filePicker}>
                                <input
                                    accept=".bin,application/octet-stream"
                                    disabled={this.isBusy}
                                    type="file"
                                    onChange={this.handleFirmwareFileChange}
                                />
                                <span className={styles.fileName}>
                                    {customFirmwareFile ? customFirmwareFile.name : '未选择 .bin，将使用网站固件'}
                                </span>
                            </label>
                        </details>
                        {(phase === PHASE_LOADING || phase === PHASE_FLASHING || progress > 0) ? (
                            <div className={styles.progressBlock}>
                                <div className={styles.progressLabels}>
                                    <span>{phase === PHASE_LOADING ? '正在校验固件' : '正在写入固件'}</span>
                                    <strong>{progress}%</strong>
                                </div>
                                <div className={styles.progressTrack}>
                                    <div
                                        className={styles.progressFill}
                                        style={{width: `${progress}%`}}
                                    />
                                </div>
                            </div>
                        ) : null}
                        <button
                            className={styles.dangerButton}
                            disabled={!canFlash}
                            type="button"
                            onClick={this.handleFlash}
                        >
                            {phase === PHASE_FLASHING ? `正在烧录 ${progress}%` : '重新安装 Domi 固件'}
                        </button>
                        {flashComplete ? (
                            <div className={styles.successBox}>
                                固件安装成功，小车已经重新启动。请继续第 3 步选择无线工作模式。
                            </div>
                        ) : null}
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.stepNumber}>3</div>
                            <div>
                                <h3>选择无线工作模式</h3>
                                <p>Wi-Fi 和蓝牙互斥运行，切换时小车会停止电机并重新启动。</p>
                            </div>
                        </div>
                        <div className={styles.wirelessModeGrid}>
                            <button
                                aria-pressed={wirelessMode === WIRELESS_MODE_WIFI}
                                className={`${styles.wirelessModeButton} ${
                                    wirelessMode === WIRELESS_MODE_WIFI ? styles.activeWirelessMode : ''
                                }`}
                                disabled={!firmwareSupportsModeSwitch || this.isBusy}
                                type="button"
                                value={WIRELESS_MODE_WIFI}
                                onClick={this.handleWirelessModeChange}
                            >
                                <strong>Wi-Fi 网页控制</strong>
                                <span>用于 PC 实时控制、摄像头和程序上传</span>
                            </button>
                            <button
                                aria-pressed={wirelessMode === WIRELESS_MODE_BLUETOOTH}
                                className={`${styles.wirelessModeButton} ${
                                    wirelessMode === WIRELESS_MODE_BLUETOOTH ? styles.activeWirelessMode : ''
                                }`}
                                disabled={!firmwareSupportsModeSwitch || this.isBusy}
                                type="button"
                                value={WIRELESS_MODE_BLUETOOTH}
                                onClick={this.handleWirelessModeChange}
                            >
                                <strong>蓝牙遥控</strong>
                                <span>关闭 Wi-Fi；等待经典蓝牙控制器连接</span>
                            </button>
                        </div>
                        {port && firmwareInfo && !firmwareSupportsModeSwitch ? (
                            <div className={styles.errorBox}>
                                检测到旧固件 v{firmwareInfo.firmwareVersion || '未知'}，它仍会同时启动 Wi-Fi 和蓝牙。
                                请先点击第 2 步“重新安装 Domi 固件”。
                            </div>
                        ) : null}
                        {port && !firmwareInfo && firmwareProbeError ? (
                            <div className={styles.errorBox}>
                                无法确认小车固件能力。请先点击第 2 步安装网站提供的新版固件。
                            </div>
                        ) : null}
                        {wirelessMode === WIRELESS_MODE_WIFI ? (
                            <div className={styles.formGrid}>
                                <label>
                                    <span>Wi-Fi 名称</span>
                                    <input
                                        maxLength={32}
                                        placeholder="例如 Domi-Classroom"
                                        type="text"
                                        value={ssid}
                                        onChange={this.handleSsidChange}
                                    />
                                </label>
                                <label>
                                    <span>Wi-Fi 密码</span>
                                    <input
                                        maxLength={63}
                                        placeholder="没有密码可以留空"
                                        type="password"
                                        value={password}
                                        onChange={this.handlePasswordChange}
                                    />
                                </label>
                            </div>
                        ) : (
                            <React.Fragment>
                                <p className={styles.modeNotice}>
                                    蓝牙模式下摄像头和网页实时控制不可用；重新选择 Wi-Fi 模式即可恢复。
                                </p>
                                {firmwareSupportsClassicBluetooth ? (
                                    <button
                                        className={styles.secondaryButton}
                                        disabled={!port || this.isBusy}
                                        type="button"
                                        onClick={this.handleClearBluetoothBonds}
                                    >
                                        清除旧蓝牙配对
                                    </button>
                                ) : null}
                            </React.Fragment>
                        )}
                        <button
                            className={styles.primaryButton}
                            disabled={!canProvision}
                            type="button"
                            onClick={this.handleWifiProvision}
                        >
                            {phase === PHASE_PROVISIONING ? '正在切换模式…' : (
                                wirelessMode === WIRELESS_MODE_WIFI ? '写入 Wi-Fi 并连接' : '启用蓝牙遥控模式'
                            )}
                        </button>
                        {phase === PHASE_DONE && wirelessMode === WIRELESS_MODE_WIFI ? (
                            <div className={styles.successBox}>
                                配置成功，小车地址：<strong>{ipAddress}</strong>。已自动保存并同步到 Domi 小车扩展，
                                现在可以拔掉 USB 开始编程。
                            </div>
                        ) : null}
                        {phase === PHASE_DONE && wirelessMode === WIRELESS_MODE_BLUETOOTH ? (
                            <div className={styles.successBox}>
                                {bluetoothBondsCleared ? (
                                    '旧蓝牙配对已清除。请在 2 分钟内重新连接名称以 DOMI-CAR- 开头的小车。'
                                ) : (
                                    <React.Fragment>
                                        蓝牙模式已启用。请在控制器中连接名称以 DOMI-CAR- 开头的小车，配对码为 1234；
                                        一段时间没有控制器连接时，小车会安全切换回 Wi-Fi。
                                    </React.Fragment>
                                )}
                            </div>
                        ) : null}
                        {ipAddress && phase !== PHASE_DONE && wirelessMode === WIRELESS_MODE_WIFI ? (
                            <div className={styles.historyBox}>
                                上次保存的小车地址：<strong>{ipAddress}</strong>。这只是历史记录，不代表小车当前在线。
                            </div>
                        ) : null}
                    </section>

                    <details
                        className={styles.logs}
                        open={phase === PHASE_ERROR}
                    >
                        <summary>安装日志</summary>
                        <pre>{logs.length ? logs.join('\n') : '等待操作…'}</pre>
                    </details>
                </div>
            </Modal>
        );
    }
}

DomiDeviceCenter.propTypes = {
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    vm: PropTypes.shape({
        runtime: PropTypes.shape({
            emit: PropTypes.func.isRequired
        }).isRequired
    }).isRequired
};

export default injectIntl(DomiDeviceCenter);
